import React from "react";
import { Message } from "primereact/message";
import * as client from "../../../client";
import styles from "./termWording.module.css";

/**
 * How a stored limit / sublimit / deductible text prints for the report's
 * policy term. The map comes from the server (see term_wording.py), derived
 * from the same rule the quote letter and PolicyFly use, so no editor rewords
 * on its own. It is keyed by the saved text, so an unsaved edit shows as typed
 * until a save rewords it.
 */
export const printedText = (
  termWording: client.TermWording,
  stored: string
): string => termWording.rewordings[stored] ?? stored;

/**
 * A stored excerpt text as it prints, with the stored text beneath when the
 * two differ. Editors bind to the stored text, so a reworded row says so.
 */
export const TermWordedValue: React.FC<{
  termWording: client.TermWording;
  stored: string;
}> = ({ termWording, stored }) => {
  const printed = printedText(termWording, stored);
  if (printed === stored) return <>{stored}</>;
  return (
    <span className={styles.wordedValue}>
      <span>{printed}</span>
      <span className={styles.storedValue}>Stored as: {stored}</span>
    </span>
  );
};

const termPeriod = (termWording: client.TermWording): string =>
  termWording.effective_date && termWording.expiration_date
    ? `runs ${termWording.effective_date} to ${termWording.expiration_date}, which is not a full year`
    : "is not a full year";

/** Explains the rewording above an excerpt editor; nothing on an annual term. */
export const TermWordingBanner: React.FC<{
  termWording: client.TermWording;
}> = ({ termWording }) => {
  if (termWording.term !== "policy") return null;
  const period = termPeriod(termWording);
  return (
    <Message
      severity="info"
      className={styles.termBanner}
      content={
        <span>
          This policy {period}, so annual wording below prints as term wording
          (for example, &ldquo;annual aggregate&rdquo; becomes &ldquo;term
          aggregate&rdquo;).
        </span>
      }
    />
  );
};

/** The banner's one-line form for a dense toolbar; nothing on an annual term. */
export const TermWordingNote: React.FC<{
  termWording: client.TermWording;
}> = ({ termWording }) => {
  if (termWording.term !== "policy") return null;
  return (
    <span className={styles.termNote}>
      <i className="pi pi-info-circle" />
      This policy {termPeriod(termWording)}, so annual wording prints as term
      wording.
    </span>
  );
};
